import { useCallback, useEffect } from "react";
import { useMutation, gql } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FlashMessage from "../../components/FlashMessage";
import UserContext from "../../context/User";
import { LoginWrapper } from "../Wrapper";
import useStyles from "./styles";
import RegistrationIcon from "../../assets/images/logo.png";
import { Avatar } from "@mui/material";
import OtpInput from "react-otp-input";
import { Link as RouterLink } from "react-router-dom";
import { requestOtp, verifyOtp, updateUser } from "../../apollo/server";
import { useTranslation } from 'react-i18next';
import ConfigurableValues from "../../config/constants";

const SEND_OTP_TO_PHONE = gql`
  ${requestOtp}
`;
const VERIFY_OTP = gql`
  ${verifyOtp}
`;
const UPDATEUSER = gql`
  ${updateUser}
`;
function VerifyPhone() {
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { state } = useLocation();
  const [seconds, setSeconds] = useState(30);
  const [otpError, setOtpError] = useState(false);
  const { profile } = useContext(UserContext);
  const { SKIP_MOBILE_VERIFICATION } = ConfigurableValues()

  const [sendOtp, { loading: loadingOtp }] = useMutation(SEND_OTP_TO_PHONE, {
    onCompleted: onOtpCompleted,
    onError: onOtpError,
  });
  const [verifyOtpMutation] = useMutation(VERIFY_OTP);

  useEffect(() => {
    const myInterval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds === 0) {
        clearInterval(myInterval);
      }
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  });

  useEffect(() => {
    const phone = state?.phone || profile?.phone;
    if (!phone) return;
    sendOtp({ variables: { identifier: phone, channel: "SMS", purpose: "PHONE" } });
    setSeconds(30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.phone, profile?.phone]);

  function onOtpError(error) {
    if (error.networkError) {
      FlashMessage({
        message: error.networkError.result.errors[0].message,
      });
    } else if (error.graphQLErrors) {
      FlashMessage({
        message: error.graphQLErrors[0].message,
      });
    }
  }
  function onOtpCompleted(data) {
    FlashMessage({
      message: "OTP sent to your phone.",
    });
  }
  function onUpdateUserError(error) {
    if (error.networkError) {
      FlashMessage({
        message: error.networkError.result.errors[0].message,
      });
    } else if (error.graphQLErrors) {
      FlashMessage({
        message: error.graphQLErrors[0].message,
      });
    }
  }

  function onUpdateUserCompleted() {
    FlashMessage({
      message: "Phone number has been verified successfully!.",
    });
  }

  const [mutate, { loading: updateUserLoading }] = useMutation(UPDATEUSER, {
    onCompleted: onUpdateUserCompleted,
    onError: onUpdateUserError,
  });

  const onCodeFilled = useCallback(async (code) => {
    const phone = state?.phone ? state.phone : profile.phone;
    // Wait for the user-update mutation so any server error blocks us instead
    // of slipping through after navigate. Only navigate back on real success.
    const finish = async () => {
      try {
        await mutate({
          variables: { name: profile.name, phone, phoneIsVerified: true },
        });
        navigate(-1, { replace: true });
      } catch (e) {
        setError(e.message || "Could not save phone number");
      }
    };
    if (SKIP_MOBILE_VERIFICATION) { await finish(); return; }
    setOtpError(false);
    setError("");
    try {
      await verifyOtpMutation({
        variables: { identifier: phone, channel: "SMS", purpose: "PHONE", code },
      });
      await finish();
    } catch (e) {
      setOtpError(true);
      setError(e.message || "Invalid Code");
    }
  },[SKIP_MOBILE_VERIFICATION, mutate, navigate, verifyOtpMutation, profile?.name, profile?.phone, state?.phone]);

  const resendOtp = () => {
    setError("");
    setOtpError(false);
    const phone = state?.phone || profile?.phone;
    sendOtp({ variables: { identifier: phone, channel: "SMS", purpose: "PHONE" } });
    setSeconds(30);
  };
  const handleCode = useCallback((val) => {
    const code = val;
    setOtp(val);
    if (code.length === 6) {
      onCodeFilled(code);
    }
  },[onCodeFilled]);

  useEffect(()=>{
    let timer = null
    if(!SKIP_MOBILE_VERIFICATION) return
    setOtp('111111')
    timer = setTimeout(()=>{
      handleCode('111111')
    },3000)
    return ()=>{timer && clearTimeout(timer)}
  },[SKIP_MOBILE_VERIFICATION,handleCode])

  return (
    <LoginWrapper>
      <FlashMessage
        open={Boolean(error)}
        severity={"error"}
        alertMessage={error}
      />
      <Box display="flex">
        <Box m="auto" sx={{ width: 90, height: 64, overflow: "hidden" }}>
          <img
            alt="RushMyFood"
            src={RegistrationIcon}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              objectPosition: "top",
              transform: "scale(1.55)",
              transformOrigin: "top center",
            }}
          />
        </Box>
      </Box>
      <Typography variant="h5" className={classes.font700}>
        {t('verifyPhone')} <br /> {t('number')}
      </Typography>
      <Box mt={theme.spacing(1)} />
      <Typography
        variant="caption"
        className={`${classes.caption} ${classes.fontGrey}`}
      >
        {t('enterOtpPhone')}
      </Typography>
      <Box display="flex">
        <Box m="auto">
          <OtpInput
            value={otp}
            onChange={handleCode}
            numInputs={6}
            containerStyle={{
              width: "100%",
              display: "flex",
              alignSelf: "center",
              backgroundColor: theme.palette.common.white,
            }}
            inputStyle={{
              width: 45,
              height: 45,
              margin: 5,
              borderRadius: 5,
              fontSize: theme.typography.h2,
              border: `1px solid ${theme.palette.grey[400]}`,
              boxShadow: theme.shadows[3],
            }}
            focusStyle={{
              outlineColor: theme.palette.grey[900],
            }}
            editable
            renderInput={(props) => <input {...props} />}
          />
          <Box mt={2} />
          {otpError && (
            <Typography variant={"h6"} style={{ color: "red", fontSize: 14 }}>
              {t('invalidCode')}
            </Typography>
          )}
        </Box>
      </Box>
      <Box mt={theme.spacing(8)} />
      <Button
        variant="contained"
        fullWidth
        type="email"
        disableElevation
        disabled={seconds !== 0}
        className={classes.btnBase}
        onClick={(e) => {
          e.preventDefault();
          resendOtp();
        }}
      >
        {loadingOtp || updateUserLoading ? (
          <CircularProgress color="primary" />
        ) : (
          <Typography
            variant="caption"
            className={`${classes.caption} ${classes.font700}`}
          >
            {t('resendCode')}
          </Typography>
        )}
      </Button>
      <Box mt={theme.spacing(2)} />
      <Typography variant="caption" className={`${classes.caption}`}>
        {seconds !== 0 ? `${t('retryAfter')} ${seconds}s` : ""}
      </Typography>
      <Box mt={theme.spacing(2)} />
      <RouterLink to="/" style={{ textDecoration: "none" }}>
        <Typography
          variant="caption"
          color="primary"
          className={`${classes.caption}`}
        >
          {t('skipNow')}
        </Typography>
      </RouterLink>
    </LoginWrapper>
  );
}

export default VerifyPhone;
